import React from 'react';
import { motion } from 'framer-motion';
import { 
  Target, 
  ShieldCheck, 
  Workflow, 
  Globe, 
  Zap, 
  Heart,
  Code,
  ArrowUpRight
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import TrustedBy from './common/TrustedBy';
import styles from './About.module.scss';


const About = () => {
  const { t } = useTranslation('about');
  // Animation Variants
  const fadeIn = {
    hidden: { opacity: 0, y: 30 },
    visible: { 
      opacity: 1, 
      y: 0, 
      transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } 
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.1
      }
    }
  };

  return (
    <div className={styles.aboutPage}>
      {/* ====== HERO SECTION ====== */}
      <section className={styles.heroSection}>
        <div className={styles.container}>
          <motion.div 
            className={styles.heroContent}
            initial="hidden"
            animate="visible"
            variants={fadeIn}
          >
            <div className={styles.heroBadge}>{t('hero.badge')}</div>
            <h1 className={styles.heroTitle}>
              {t('hero.title_line_1')} <br />
              <span>{t('hero.title_line_2')}</span>
            </h1>
            <p className={styles.heroSubtitle}>
              {t('hero.subtitle')} <strong> {t('hero.subtitle_strong')}</strong>
            </p>
          </motion.div>
        </div>
        <div className={styles.heroVisual}>
            <div className={styles.overlay} />
            <img 
              src="/assets/images/luxury_editorial_hero.jpg" 
              alt="Team collaboration" 
            />
        </div>
      </section>

      {/* ====== MISSION & VALUES ====== */}
      <section className={styles.missionSection}>
        <div className={styles.container}>
          <div className={styles.missionGrid}>
            <motion.div 
               className={styles.missionText}
               initial="hidden"
               whileInView="visible"
               viewport={{ once: true }}
               variants={fadeIn}
            >
              <h2 className={styles.sectionTitle}>{t('mission.title')}</h2>
              <p className={styles.sectionLead}>
                {t('mission.lead')}
              </p>
              <p className={styles.sectionDesc}>
                {t('mission.description')}
              </p>
            </motion.div>
            
            <motion.div 
              className={styles.valuesGrid}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={staggerContainer}
            >
              {[
                { 
                  icon: <Target size={24} />, 
                  title: t('mission.values.precision.title'),
                  desc: t('mission.values.precision.description')
                },
                { 
                  icon: <ShieldCheck size={24} />, 
                  title: t('mission.values.curation.title'),
                  desc: t('mission.values.curation.description')
                },
                { 
                  icon: <Zap size={24} />, 
                  title: t('mission.values.infrastructure.title'),
                  desc: t('mission.values.infrastructure.description')
                },
                { 
                  icon: <Globe size={24} />, 
                  title: t('mission.values.data.title'),
                  desc: t('mission.values.data.description')
                }
              ].map((value, i) => (
                <motion.div key={i} className={styles.valueCard} variants={fadeIn}>
                  <div className={styles.valueIcon}>{value.icon}</div>
                  <h3>{value.title}</h3>
                  <p>{value.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====== THE PROBLEM SECTION ====== */}
      <section className={styles.problemSection}>
        <div className={styles.container}>
          <motion.div 
            className={styles.problemContent}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
             <h2 className={styles.sectionTitleCentered}>{t('problem.title')}</h2>
            <div className={styles.problemVisual}>
               <div className={styles.problemSide}>
                  <h4 className={styles.problemLabel}>{t('problem.open_market.title')}</h4>
                  <ul className={styles.problemList}>
                    {t('problem.open_market.items', { returnObjects: true }).map((item) => <li key={item}>{item}</li>)}
                  </ul>
               </div>
               <div className={styles.separator} />
               <div className={styles.fothSide}>
                  <h4 className={styles.fothLabel}>{t('problem.standard.title')}</h4>
                  <ul className={styles.fothList}>
                    {t('problem.standard.items', { returnObjects: true }).map((item) => <li key={item}>{item}</li>)}
                  </ul>
               </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* ====== VISION & FUTURE ====== */}
      <section className={styles.visionSection}>
        <div className={styles.container}>
          <div className={styles.visionBox}>
            <motion.div 
              className={styles.visionContent}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              variants={fadeIn}
            >
              <div className={styles.visionIcon}><Workflow size={40} /></div>
              <h2>{t('vision.title')}</h2>
              <p>
                {t('vision.description')}
              </p>
              <div className={styles.statsGrid}>
                <div className={styles.statItem}>
                  <strong>{t('vision.stats.first.label')}</strong>
                  <span>{t('vision.stats.first.value')}</span>
                </div>
                <div className={styles.statItem}>
                  <strong>{t('vision.stats.second.label')}</strong>
                  <span>{t('vision.stats.second.value')}</span>
                </div>
                <div className={styles.statItem}>
                  <strong>{t('vision.stats.third.label')}</strong>
                  <span>{t('vision.stats.third.value')}</span>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ====== TEAM / CULTURE (Conceptual) ====== */}
      <section className={styles.cultureSection}>
        <div className={styles.container}>
           <motion.div 
             className={styles.cultureHeader}
             initial="hidden"
             whileInView="visible"
             viewport={{ once: true }}
             variants={fadeIn}
           >
              <h2 className={styles.sectionTitle}>{t('culture.title')}</h2>
              <p className={styles.sectionDesc}>
                {t('culture.description')}
              </p>
           </motion.div>
           
           <div className={styles.cultureGrid}>
              <motion.div className={styles.cultureCard} variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                 <Code size={32} />
                 <h3>{t('culture.technology.title')}</h3>
                 <p>{t('culture.technology.description')}</p>
              </motion.div>
              <motion.div className={styles.cultureCard} variants={fadeIn} initial="hidden" whileInView="visible" viewport={{ once: true }}>
                 <Heart size={32} />
                 <h3>{t('culture.community.title')}</h3>
                 <p>{t('culture.community.description')}</p>
              </motion.div>
           </div>
        </div>
      </section>

      {/* ====== CLIENTS & PARTNERS ====== */}
      <TrustedBy />

      {/* ====== FINAL CTA ====== */}
      <section className={styles.finalCta}>
        <div className={styles.container}>
          <motion.div 
            className={styles.ctaCard}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeIn}
          >
            <div className={styles.ctaEyebrow}><span className={styles.ctaDot} />{t('cta.eyebrow')}</div>
            <h2>{t('cta.title_line_1')} <br /><span>{t('cta.title_line_2')}</span></h2>
            <p>{t('cta.description')}</p>
            <div className={styles.ctaFooter}>
              <span className={styles.ctaNote}>{t('cta.note')}</span>
              <Link to="/register" className={styles.primaryBtn}>
                {t('cta.button')} <ArrowUpRight size={17} />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default About;
